import type { ResourceType, TransformRequestFunction } from 'mapbox-gl';

import BaseService from '../common/BaseService';

/**
 * Destino que falla al momento: el dominio .invalid está reservado (RFC 6761)
 * y nunca resuelve, así que la petición acaba en un error de red inmediato.
 */
const FAILING_URL = 'https://map-fault-injection.invalid/';

/**
 * Destino que no responde: 192.0.2.0/24 (TEST-NET-1, RFC 5737) es solo para
 * documentación y no se enruta. La conexión ni se establece ni se rechaza, y
 * la petición queda colgada hasta que el sistema la abandona, normalmente
 * pasado uno o dos minutos.
 *
 * No sirven 127.0.0.1 ni las IPs privadas: si la página no es local (la PWA),
 * Chrome puede bloquearlas al momento por Local Network Access y la petición
 * no llegaría a colgarse.
 */
const HANGING_URL = 'https://192.0.2.1/map-fault-injection';

type FaultMode = 'style-fail' | 'style-hang' | 'tile-fail' | 'tile-fail-late' | 'source-fail';

interface Fault {
    /** Tipo de petición de Mapbox que se sabotea. */
    resourceType: ResourceType;
    /** URL por la que se sustituye la original. */
    url: string;
}

const faults: Record<FaultMode, Fault> = {
    // El estilo falla: Mapbox emite error y no vuelve a intentarlo.
    'style-fail': { resourceType: 'Style', url: FAILING_URL },
    // El estilo no responde: no llega ni load ni error mientras siga colgado.
    'style-hang': { resourceType: 'Style', url: HANGING_URL },
    // Las teselas fallan al momento, como al perder la red a media carga.
    // Mapbox las da por terminadas, el load llega y la app pone controles y
    // marcadores, pero no vuelve a pedirlas mientras sigan a la vista.
    'tile-fail': { resourceType: 'Tile', url: FAILING_URL },
    // Las teselas fallan tarde, después del último repintado, y load/idle
    // pueden no llegar nunca (maplibre-gl-js#8716, igual en Mapbox).
    'tile-fail-late': { resourceType: 'Tile', url: HANGING_URL },
    // Falla el TileJSON de las fuentes del estilo: la fuente ya no pide
    // ninguna tesela y el mapa base se queda vacío hasta recargar.
    'source-fail': { resourceType: 'Source', url: FAILING_URL },
};

/**
 * SOLO PARA PRUEBAS. Inyecta fallos de red en las peticiones de Mapbox para
 * reproducir el mapa que no termina de cargar con mala cobertura.
 *
 * Se activa desde src/config.json. config.default.json trae las claves con los
 * valores que lo dejan apagado ("" y 0):
 *
 *   "debug_map_fault_injection": "style-fail" | "style-hang" | "tile-fail"
 *                                | "tile-fail-late" | "source-fail",
 *   "debug_map_fault_injection_count": 1,
 *   "debug_map_fault_injection_seconds": 20
 *
 * debug_map_fault_injection_count limita cuántas peticiones de ese tipo se sabotean
 * desde que arranca la app; con 0, todas. Con 1 en tile-fail-late solo falla
 * una tesela, y el mapa se ve entero pero sin los controles, que se añaden en
 * el evento load. En source-fail conviene 0: el estilo también pide el
 * TileJSON de la capa satélite, que está oculta, y con 1 podría ser el único
 * saboteado.
 *
 * debug_map_fault_injection_seconds limita la inyección a los primeros segundos
 * desde que se crea el mapa, como una red que se cae durante la carga y luego
 * vuelve; con 0, sin límite. Solo decide qué peticiones se sabotean al salir:
 * las que ya están colgadas (style-hang, tile-fail-late) siguen colgadas.
 *
 * No es un simulacro: se cambia la URL por un destino que falla o no responde,
 * el navegador hace la petición de verdad y Mapbox recorre el mismo código que
 * ante un corte de red real.
 *
 * Los avisos van por console.warn y no por loglevel: el nivel guardado en
 * localStorage ('loglevel') manda sobre app_loglevel y puede silenciarlos, y
 * con la inyección activa tienen que verse siempre.
 */
class MapFaultInjectionService extends BaseService {

    /** Peticiones saboteadas desde el arranque, para debug_map_fault_injection_count. */
    static injected = 0;

    /**
     * Opción transformRequest del Map.
     *
     * @returns undefined si la inyección de fallos no está configurada, que es
     *   lo normal, y entonces Mapbox no transforma ninguna petición.
     */
    static getTransformRequest(): TransformRequestFunction | undefined {
        const {
            debug_map_fault_injection: mode,
            debug_map_fault_injection_count: count,
            debug_map_fault_injection_seconds: seconds,
        } = this.config();
        if (!mode) {
            return undefined;
        }
        if (!this.isFaultMode(mode)) {
            console.warn(`debug_map_fault_injection "${mode}" no existe y se ignora. Modos: ${Object.keys(faults).join(', ')}`);
            return undefined;
        }

        const fault = faults[mode];
        // 0, el valor por defecto, no limita: un entorno que solo active el
        // modo heredaría ese 0 y no sabotearía nada.
        const limit = count || Infinity;
        // Igual que el contador, 0 no limita.
        const deadline = seconds ? Date.now() + seconds * 1000 : Infinity;
        console.warn(`Inyección de fallos ACTIVA en el mapa: modo ${mode}, peticiones ${fault.resourceType} afectadas: ${count || 'todas'}${seconds ? `, durante ${seconds} s` : ''}`);
        if (seconds) {
            setTimeout(() => {
                console.warn(`Fin de la inyección de fallos tras ${seconds} s: las peticiones vuelven a salir normales`);
            }, seconds * 1000);
        }

        return (url, resourceType) => {
            if (resourceType !== fault.resourceType || this.injected >= limit || Date.now() >= deadline) {
                return { url };
            }
            this.injected++;
            // Sin la query, que lleva el access token.
            console.warn(`Fallo inyectado (${mode}) en ${url.split('?')[0]}`);
            return { url: fault.url };
        };
    }

    static isFaultMode(mode: string): mode is FaultMode {
        return Object.keys(faults).includes(mode);
    }
}

export default MapFaultInjectionService;
